import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { helmetJsonLdProp } from 'react-schemaorg';
import { prepareGalleryImages, useGalleryContext } from '@yme/atoms/gallery';
import { PRICE_EXTRA_INFORMATION, PRICE_EXTRA_INFORMATION_TOOLTIP_TEXT, ProductType, UK_PREFIX, } from '@yme/common/constants';
import { useIsMount, useLocation, useWindowSize } from '@yme/common/hooks';
import { closeCartModal } from '@yme/common/redux/cart/actions';
import { getCartEntries, getCartErrorData, getIsAddingProduct, getIsLoadingExternalCart, getIsOpenModal, getIsOpenModalExternal, } from '@yme/common/redux/cart/selectors';
import { setCurrentSku } from '@yme/common/redux/common/action';
import { setClearFilterOptions, setPriceHeroInfo, setProductCardLabels, setYouFinanceText, getProductBenefits, getProductFilterOptions, selectProductCardData, } from '@yme/pdp-components/ProductCard';
import { getCartModalLabels } from '@yme/pdp-components/ProductCard/components/CartModalWindow';
import { GALLERY_CONFIG } from '@yme/pdp-components/ProductCard/constants';
import { getLabels, getProductImagesByIndex } from '@yme/pdp-components/ProductCard/helpers';
import { getYoutubeIdList } from '@yme/pdp-components/ProductVideo/selectors';
import { getBloomreachModel, getCategoryTitle, getCommon, getCommonLabels, getIsInAuthor, getRTL, } from '@yme/selectors/common';
import { COLOR_NAME_KEY, E_COMMERCE_ENABLED_KEY, GENDER_KEY, PRODUCT_PDP_META_DESCRIPTION, PRODUCT_PDP_META_TITLE, SIZE_KEY, STORY_BODY_A_KEY, } from '@yme/selectors/constants';
import { getAttribute, getFullImagesList, getIsLoadedProductDetails, getProductBlockStrategy, getProductDetails, getProductDetailsVariantsWithGroupingProducts, getProductVariantsByColorWithGroupingProducts, } from '@yme/selectors/products';
import { PixelPageView, setPixelPageView } from '@yme/utils/bloomreach';
import { DigitalDataMutation, getAttributeByName, selectColorTheme, setDigitalData, } from '@yme/utils/common';
import { GalleryItemType, MAX_STOCK_VALUE, YAMAHA_LOGO_LINK } from '@yme/utils/constants';
import { ANALYTIC_RELATED_PRODUCT_KEY } from '../CustomersAlsoBought/constants';
export const useProductCardData = ({ labels, paymentOptions, smartfitKey, priceExtraInfo, priceExtraInfoTooltip, priceHeroInfo, hyperdriveEndpoints, galleryConfig = GALLERY_CONFIG, is360ImageViewed = true, motorcyclesInfoLink, scooterPackagesInfoLink, }) => {
    const [currentSlug, setCurrentSlug] = useState('');
    const categoryTitle = useSelector(getCategoryTitle);
    const dispatch = useDispatch();
    const productCardData = useSelector(selectProductCardData);
    const isRightToLeft = useSelector(getRTL);
    const cartErrorData = useSelector(getCartErrorData);
    const isAddingProduct = useSelector(getIsAddingProduct);
    const isLoadingExternalCart = useSelector(getIsLoadingExternalCart);
    const isOpenCartModal = useSelector(getIsOpenModal);
    const isOpenExternalCartModal = useSelector(getIsOpenModalExternal);
    const { language, ecommerceId } = useSelector(getCommon);
    const cartEntries = useSelector(getCartEntries);
    const { colorItemIndex, sku } = useSelector(getProductFilterOptions);
    const productVariantsByColor = useSelector(getProductVariantsByColorWithGroupingProducts);
    const productDetailsVariants = useSelector(getProductDetailsVariantsWithGroupingProducts);
    const { data: productDetailsData } = useSelector(getProductDetails);
    const productBlockStrategy = useSelector(getProductBlockStrategy);
    const productBenefits = useSelector(getProductBenefits);
    const commonLabels = useSelector(getCommonLabels);
    const youTubeIdList = useSelector(getYoutubeIdList);
    const fullImagesList = useSelector(getFullImagesList);
    const isLoadedProductDetails = useSelector(getIsLoadedProductDetails);
    const { productCatalog } = useSelector(getBloomreachModel);
    const isInAuthor = useSelector(getIsInAuthor);
    const cartModalLabels = getCartModalLabels(labels, commonLabels);
    const view360Button = useRef(null);
    const galleryContext = useGalleryContext();
    const isMount = useIsMount();
    const { hash: skuFromLocation } = useLocation();
    const isTracked = useRef('');
    const isTrackedPixel = useRef('');
    const { isMobileView, isTabletView } = useWindowSize();
    const productCardLabels = getLabels(labels);
    const targetSku = skuFromLocation || sku;
    const galleryImages = getProductImagesByIndex(productVariantsByColor, targetSku, colorItemIndex);
    const currentCartEntry = cartEntries.find((entry) => entry.product.variants.find((variant) => variant.sku === targetSku));
    const currentColorTheme = productBlockStrategy
        ? selectColorTheme(productBlockStrategy?.value)
        : null;
    const { deliveryText, returnText, warrantyText } = productBenefits;
    const isEmptyProductBenefits = Object.values(productBenefits).every((value) => !value);
    const isMaxStockValue = currentCartEntry ? currentCartEntry.quantity >= MAX_STOCK_VALUE : false;
    const galleryItems = useMemo(() => prepareGalleryImages(fullImagesList, galleryImages, youTubeIdList, view360Button, is360ImageViewed, isMobileView, isTabletView), [fullImagesList, galleryImages, youTubeIdList, is360ImageViewed, isMobileView, isTabletView]);
    const galleryComponentProps = {
        config: galleryConfig,
        view360BtnRef: view360Button,
    };
    const variant = productDetailsVariants?.find((item) => item.sku === targetSku);
    const metaDescription = variant
        ? getAttributeByName(variant.attributes, PRODUCT_PDP_META_DESCRIPTION)
        : '';
    const metaTitle = variant ? getAttributeByName(variant.attributes, PRODUCT_PDP_META_TITLE) : '';
    const structuredData = useMemo(() => {
        if (!productDetailsData || !productDetailsData.published) {
            return {};
        }
        const variant = productDetailsVariants?.find((item) => item.sku === targetSku);
        const masterVariant = productDetailsVariants?.find((item) => item.master === true);
        if (!variant || !masterVariant) {
            return {};
        }
        const inStock = variant.stock > 0;
        const isUnit = productDetailsData.type === ProductType.UNIT;
        const isApparel = productDetailsData.type === ProductType.APPAREL;
        const price = variant.prices[0];
        if (!price) {
            return {};
        }
        const hasECommerceEnabled = getAttributeByName(variant.attributes, E_COMMERCE_ENABLED_KEY);
        if (!hasECommerceEnabled) {
            return {};
        }
        const hasDiscount = 'discount' in price && price.discount?.discountedPrice;
        const priceValue = hasDiscount ? price.discount?.discountedPrice : price.amount;
        const color = getAttributeByName(variant.attributes, COLOR_NAME_KEY) ?? '';
        const gender = getAttributeByName(variant.attributes, GENDER_KEY) ?? '';
        const size = getAttributeByName(variant.attributes, SIZE_KEY) ?? '';
        const image = galleryItems.imagesList?.find((item) => {
            return item.type === GalleryItemType.Studio;
        })?.id ?? '';
        const masterStudioImage = masterVariant.images.find((item) => item.url.toLowerCase().includes('studio-001'))?.url;
        const variantStudioImage = variant.images.find((item) => item.url.toLowerCase().includes('studio-001'))?.url;
        const description = !isUnit
            ? productDetailsData.description
            : getAttributeByName(variant.attributes, STORY_BODY_A_KEY) ?? '';
        const canonicalUrl = document.head.querySelector('link[rel="canonical"]')?.getAttribute('href') ||
            window.location.href;
        const isGB = window.location.pathname.startsWith(UK_PREFIX);
        const productData = {
            '@context': 'https://schema.org',
            '@type': 'Product',
            name: productDetailsData.name,
            sku: variant.sku,
            image: variantStudioImage ?? masterStudioImage ?? image ?? '',
            description,
            category: categoryTitle,
            color,
            offers: {
                '@type': 'Offer',
                availability: inStock ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
                url: isGB ? canonicalUrl : window.location.href,
                price: String(priceValue),
                priceCurrency: price.currencyCode,
            },
            brand: {
                '@type': 'Brand',
                name: 'Yamaha',
                logo: YAMAHA_LOGO_LINK,
            },
        };
        const sizeRef = {
            '@type': 'SizeSpecification',
            suggestedGender: gender,
            name: size,
        };
        if (!isApparel) {
            delete sizeRef.suggestedGender;
        }
        if (size || gender) {
            productData.size = sizeRef;
        }
        const HelmetProps = helmetJsonLdProp(productData);
        return HelmetProps;
    }, [productDetailsData, galleryItems, productDetailsVariants, targetSku, categoryTitle]);
    const analyticRelatedProductData = sessionStorage.getItem(ANALYTIC_RELATED_PRODUCT_KEY);
    const analyticRelatedProduct = useMemo(() => analyticRelatedProductData
        ? JSON.parse(analyticRelatedProductData)
        : null, [analyticRelatedProductData]);
    useEffect(() => {
        const { setGalleryData } = galleryContext;
        if (setGalleryData && galleryItems?.imagesList) {
            setGalleryData(galleryItems);
        }
    }, [galleryItems, galleryContext]);
    useEffect(() => {
        if (ecommerceId?.slug && currentSlug !== ecommerceId.slug) {
            setCurrentSlug(ecommerceId.slug);
            dispatch(setClearFilterOptions());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ecommerceId?.slug, dispatch]);
    useEffect(() => {
        if (!isMount && skuFromLocation) {
            const isSkuExist = productDetailsVariants?.some((item) => item.sku === skuFromLocation);
            if (isSkuExist) {
                dispatch(setCurrentSku(skuFromLocation));
            }
        }
    }, [dispatch, isMount, skuFromLocation, productDetailsVariants]);
    useEffect(() => {
        if (labels) {
            dispatch(setProductCardLabels(labels));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);
    useEffect(() => {
        if (productCardLabels) {
            // TODO: (D.K.) Need refactor this case
            // (better remove this functionality / push common labels into PDP labels)
            dispatch(setProductCardLabels(productCardLabels));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);
    useEffect(() => {
        if (productDetailsData && sku && isTracked.current !== productDetailsData.id) {
            isTracked.current = productDetailsData.id;
            setDigitalData(DigitalDataMutation.PDP_VIEW, {
                product: productDetailsData,
                currentSku: sku,
            });
            if (analyticRelatedProduct?.previousAnalyticProduct &&
                productDetailsData.id === analyticRelatedProduct?.relatedProductId) {
                setDigitalData(DigitalDataMutation.CLICK_FROM_RELATED_WIDGET, {
                    product: analyticRelatedProduct.previousAnalyticProduct,
                });
                sessionStorage.removeItem(ANALYTIC_RELATED_PRODUCT_KEY);
            }
            else if (productDetailsData.id === analyticRelatedProduct?.previousAnalyticProduct?.id) {
                return;
            }
            else {
                sessionStorage.removeItem(ANALYTIC_RELATED_PRODUCT_KEY);
            }
        }
    }, [analyticRelatedProduct, productDetailsData, sku]);
    useEffect(() => {
        if (productDetailsData &&
            targetSku &&
            isTrackedPixel.current !== productDetailsData.key &&
            !isInAuthor) {
            isTrackedPixel.current = productDetailsData.key;
            setPixelPageView(PixelPageView.PRODUCT_PAGE, {
                prod_id: productDetailsData.key,
                prod_name: productDetailsData.name,
                sku: targetSku,
                domain_key: productCatalog,
            });
        }
    }, [isLoadedProductDetails, productDetailsData, targetSku, productCatalog, isInAuthor]);
    const onHideModalCart = () => {
        dispatch(closeCartModal());
    };
    const youFinanceText = useSelector(getAttribute(PRICE_EXTRA_INFORMATION)) || priceExtraInfo;
    const youFinanceHint = useSelector(getAttribute(PRICE_EXTRA_INFORMATION_TOOLTIP_TEXT)) || priceExtraInfoTooltip;
    useEffect(() => {
        dispatch(setYouFinanceText(youFinanceText ?? ''));
        dispatch(setPriceHeroInfo(priceHeroInfo ?? ''));
    }, [youFinanceText, dispatch, priceHeroInfo]);
    const cardPanelComponentProps = {
        labels,
        language,
        priceExtraInfo: youFinanceText,
        priceExtraInfoTooltip: youFinanceHint,
        productVariantsByColor,
        isMaxStockValue,
        hyperdriveEndpoints,
        paymentOptions,
        smartfitKey,
        productType: productDetailsData?.type,
        motorcyclesInfoLink,
        scooterPackagesInfoLink,
    };
    const productBenefitsProps = {
        deliveryInfo: deliveryText,
        returnInfo: returnText,
        warrantyInfo: warrantyText,
    };
    return {
        onHideModalCart,
        galleryContext,
        galleryComponentProps,
        cardPanelComponentProps,
        productBenefitsProps,
        productCardLabels,
        currentColorTheme,
        cartModalLabels,
        productCardData,
        isRightToLeft,
        cartErrorData,
        isAddingProduct,
        isLoadingExternalCart,
        isOpenCartModal,
        isEmptyProductBenefits,
        isOpenExternalCartModal,
        structuredData,
        metaDescription,
        metaTitle,
        isLoadedProductDetails,
    };
};
